import React, { useState } from "react";
import {
  Modal,
  Box,
  Typography,
  Grid,
  Paper,
  Divider,
  Chip,
  Button,
  CircularProgress,
  Tooltip,
  IconButton,
  Collapse,
  Link
} from "@mui/material";
import {
  Refresh,
  Visibility,
  Download,
  PictureAsPdf,
  LocalShipping,
  CheckCircle,
  Pending,
  Cancel,
  Inventory,
  Info,
  ExpandMore,
  ExpandLess,
  Receipt,
  AttachMoney
} from "@mui/icons-material";
import { useQuery, gql } from "@apollo/client";
import "../styles/OrderPage.css";

// New GraphQL query with all required fields
const GET_ALL_ORDERS = gql`
  query {
    orders {
      orderId
      orderType
      buyer {
        buyerId
        name
        email
        phone
        address {
          street
          city
          state
          postalCode
          country
          countryCode
        }
      }
      items {
        productId
        type
        name
        price
        quantity
        sellerId
        seller {
          customId
          firebaseUid
          name
          companyName
          gstNumber
          bankIFCnumber
          bankAccountNumber
          authorized
          email
          phoneNumber
          status
          companyPan
          bankName
          shiprocketNickname
          pickupPincode
          pickupCity
          pickupState
          pickupHouseNo
          pickupAddress
          shiprocketPickupId
          shiprocketSyncSuccess
          shiprocketSyncCompletedAt
          shiprocketSyncAttempts
          shiprocketVerificationStatus
          shiprocketSyncError
          razorpayVerified
          shiprocketVerifiedAt
          razorpayContactId
          razorpayFundAccountId
          razorpayValidationId
          razorpayVerificationPaymentId
          bankVerifiedName
          razorpayStatus
          razorpayVerifiedAt
          fcmTokens
          fcmToken
          otp
          otpExpiresAt
          otpVerified
          createdAt
          updatedAt
          deactivatedAt
          deactivatedReason
        }
        image
        itemStatus
        cancelReason
        rejectReason
        dimension {
          weight
          length
          breadth
          height
        }
        shippingCost
        shipment {
          provider
          shipmentId
          shiprocketOrderId
          courierId
          awbCode
          trackingUrl
          shipmentStatus
          shippedAt
          deliveredAt
          packedAt
          shiprocketLabel
          labelGeneratedAt
          shiprocketManifest
          manifestGeneratedAt
          estimatedDelivery
        }
        invoice {
          invoiceNo
          invoiceUrl
          generatedAt
        }
        payout {
          payoutId
          payoutAmount
          paidAt
        }
      }
      totalAmount
      currency
      exchangeRate
      shipmentId
      pricing {
        subtotal
        taxAmount
        shippingCost
        discountAmount
        totalAmount
      }
      orderStatus
      payment {
        razorpayOrderId
        razorpayPaymentId
        razorpaySignature
        mode
        provider
        method
        paymentStatus
        transactionId
        paidAt
      }
      commission {
        percentage
        amount
      }
      sellerEarning
      payoutStatus
      shippingMethod
      estimatedDelivery
      returns {
        productId
        sellerId
        reason
        images
        status
        refundStatus
        refundId
        refundAmount
        requestedAt
        approvedAt
        pickedUpAt
        receivedAt
        refundedAt
        shiprocket {
          returnOrderId
          returnShipmentId
          channel_order_id
          status
          company_name
        }
      }
      customs {
        hsCode
        countryOfOrigin
        description
        declaredValue
      }
      returnRequest {
        isRequested
        reason
        approved
        refundAmount
        requestedAt
      }
      createdAt
      updatedAt
    }
  }
`;

const ITEMS_PER_PAGE = 10;

export default function AdminOrderPage() {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [expandedOrders, setExpandedOrders] = useState(new Set());
  const [expandedSellerDetails, setExpandedSellerDetails] = useState(new Set());

  const { loading, error, data, refetch } = useQuery(GET_ALL_ORDERS, {
    pollInterval: 10000,
    fetchPolicy: "network-only",
  });

  if (error) {
    console.error("GraphQL Error Details:", error);
    return (
      <div className="order-sheet-container">
        <div style={{ color: 'red', padding: '20px', textAlign: 'center' }}>
          <h3>Error Loading Orders</h3>
          <p>{error.message}</p>
          <button
            className="order-btn-primary"
            onClick={() => refetch()}
            style={{ marginTop: '20px' }}
          >
            <Refresh /> Try Again
          </button>
        </div>
      </div>
    );
  }

  const orders = data?.orders || [];

  // Get unique item statuses for filtering
  const getAllItemStatuses = () => {
    const statuses = new Set();
    orders.forEach(order => {
      order.items?.forEach(item => {
        if (item.itemStatus) {
          statuses.add(item.itemStatus);
        }
      });
    });
    return Array.from(statuses).sort();
  };

  // Filter orders based on item status and search term
  const filteredOrders = orders.filter((order) => {
    const query = searchTerm.toLowerCase();
    const matchesSearch =
      order.orderId.toLowerCase().includes(query) ||
      (order.buyer?.name || "").toLowerCase().includes(query) ||
      (order.buyer?.email || "").toLowerCase().includes(query);

    let matchesStatus = true;
    if (statusFilter !== "all") {
      matchesStatus = order.items?.some(
        item => item.itemStatus?.toLowerCase() === statusFilter.toLowerCase()
      );
    }

    return matchesSearch && matchesStatus;
  });

  // Order-level summary status
  const getOrderSummaryStatus = (order) => {
    if (!order.items || order.items.length === 0) return "NO_ITEMS";
    const statuses = order.items.map(item => item.itemStatus);
    const allSame = statuses.every(s => s === statuses[0]);
    if (allSame) return statuses[0];

    const hasDelivered = statuses.includes("DELIVERED");
    const hasShipped = statuses.includes("SHIPPED");
    const hasPacked = statuses.includes("PACKED");
    const hasPending = statuses.includes("PENDING");
    const hasCancelled = statuses.includes("CANCELLED");

    if (hasDelivered && hasShipped) return "PARTIALLY_DELIVERED";
    if (hasShipped && hasPending) return "PARTIALLY_SHIPPED";
    if (hasPacked && hasPending) return "PARTIALLY_PACKED";
    if (hasCancelled && (hasPending || hasPacked)) return "PARTIALLY_CANCELLED";
    return "MIXED";
  };

  const getOrderStatusIcon = (summaryStatus) => {
    switch (summaryStatus) {
      case "DELIVERED": return <CheckCircle sx={{ fontSize: 18, color: '#10b981' }} />;
      case "SHIPPED": return <LocalShipping sx={{ fontSize: 18, color: '#3b82f6' }} />;
      case "PACKED": return <Inventory sx={{ fontSize: 18, color: '#8b5cf6' }} />;
      case "PENDING": return <Pending sx={{ fontSize: 18, color: '#f59e0b' }} />;
      case "CANCELLED": return <Cancel sx={{ fontSize: 18, color: '#ef4444' }} />;
      default: return <Info sx={{ fontSize: 18, color: '#6b7280' }} />;
    }
  };

  const paginatedOrders = filteredOrders.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const totalPages = Math.ceil(filteredOrders.length / ITEMS_PER_PAGE);
  const hasNextPage = currentPage < totalPages;
  const hasPreviousPage = currentPage > 1;

  const stats = {
    total: orders.length,
    totalAmount: orders.reduce(
      (sum, order) => sum + (order.pricing?.totalAmount || 0),
      0
    ),
    totalItems: orders.reduce(
      (sum, order) => sum + (order.items?.length || 0),
      0
    ),
    pendingItems: orders.reduce(
      (sum, order) => sum + (order.items?.filter(i => i.itemStatus === "PENDING").length || 0),
      0
    ),
    shippedItems: orders.reduce(
      (sum, order) => sum + (order.items?.filter(i => i.itemStatus === "SHIPPED").length || 0),
      0
    ),
    deliveredItems: orders.reduce(
      (sum, order) => sum + (order.items?.filter(i => i.itemStatus === "DELIVERED").length || 0),
      0
    ),
  };

  const handleRefresh = () => {
    refetch();
  };

  const exportOrders = () => {
    if (filteredOrders.length === 0) return;

    const headers = [
      "Order ID", "Buyer", "Email", "Total Amount", "Items Status",
      "Item Names", "Seller Names", "Tracking Numbers", "Date"
    ];
    const csvContent = [
      headers.join(","),
      ...filteredOrders.map((order) => {
        const itemStatuses = order.items?.map(i => `${i.name}:${i.itemStatus}`).join("; ");
        const itemNames = order.items?.map(i => i.name).join("; ");
        const sellerNames = order.items?.map(i => i.seller?.name || i.sellerId).join("; ");
        const trackingNumbers = order.items?.map(i => i.shipment?.awbCode || "").join("; ");
        return [
          order.orderId,
          order.buyer?.name || "N/A",
          order.buyer?.email || "N/A",
          order.pricing?.totalAmount || 0,
          `"${itemStatuses}"`,
          `"${itemNames}"`,
          `"${sellerNames}"`,
          `"${trackingNumbers}"`,
          new Date(order.createdAt).toLocaleDateString(),
        ].join(",");
      }),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `orders_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
  };

  const getStatusClass = (status) => {
    const s = status?.toLowerCase();
    if (["pending"].includes(s)) return "status-pending";
    if (["packed"].includes(s)) return "status-packed";
    if (["shipped"].includes(s)) return "status-shipped";
    if (["delivered"].includes(s)) return "status-delivered";
    if (["cancelled", "rejected"].includes(s)) return "status-cancelled";
    return "";
  };

  const toggleOrderExpand = (orderId) => {
    const newExpanded = new Set(expandedOrders);
    if (newExpanded.has(orderId)) {
      newExpanded.delete(orderId);
    } else {
      newExpanded.add(orderId);
    }
    setExpandedOrders(newExpanded);
  };

  if (loading && orders.length === 0) {
    return (
      <div className="order-sheet-container">
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '400px' }}>
          <CircularProgress size={60} />
          <p style={{ marginTop: '20px', color: '#1a365d', fontWeight: '500' }}>Loading orders inventory...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="order-sheet-container">
      {/* Header Section */}
      <div className="order-sheet-header">
        <h1>
          📦 Order Management
          <span className="order-sheet-subtitle">Manage and track all customer orders with item-level status</span>
        </h1>

        <div className="order-sheet-actions">
          <div className="order-search-box">
            <input
              type="text"
              placeholder="Search orders by ID, buyer name, email..."
              className="order-sheet-search"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>

          <div className="order-action-buttons">
            <select
              className="order-filter-select"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="all">All Item Status</option>
              <option value="pending">Pending Items</option>
              <option value="packed">Packed Items</option>
              <option value="shipped">Shipped Items</option>
              <option value="delivered">Delivered Items</option>
              <option value="cancelled">Cancelled Items</option>
              <option value="rejected">Rejected Items</option>
            </select>

            <button className="order-btn-secondary" onClick={handleRefresh}>
              <Refresh /> Refresh
            </button>
            <button className="order-btn-primary" onClick={exportOrders}>
              <Download /> Export
            </button>
          </div>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="order-sheet-summary">
        <div className="order-summary-card total">
          <span className="order-summary-label">Total Orders</span>
          <span className="order-summary-value">{stats.total}</span>
        </div>
        <div className="order-summary-card value">
          <span className="order-summary-label">Total Value</span>
          <span className="order-summary-value">₹{stats.totalAmount.toLocaleString()}</span>
        </div>
        <div className="order-summary-card pending">
          <span className="order-summary-label">Pending Items</span>
          <span className="order-summary-value">{stats.pendingItems}</span>
        </div>
        <div className="order-summary-card shipped">
          <span className="order-summary-label">Shipped Items</span>
          <span className="order-summary-value">{stats.shippedItems}</span>
        </div>
        <div className="order-summary-card delivered">
          <span className="order-summary-label">Delivered Items</span>
          <span className="order-summary-value">{stats.deliveredItems}</span>
        </div>
      </div>

      {/* Orders Table with Expandable Rows */}
      <div className="order-table-container">
        <div className="order-table-wrapper">
          <table className="order-sheet-table">
            <thead>
              <tr>
                <th style={{ width: '40px' }}></th>
                <th>Order ID</th>
                <th>Buyer Details</th>
                <th>Amount</th>
                <th>Items Status</th>
                <th>Payment</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedOrders.length > 0 ? (
                paginatedOrders.map((order) => {
                  const summaryStatus = getOrderSummaryStatus(order);
                  const isExpanded = expandedOrders.has(order.orderId);

                  return (
                    <React.Fragment key={order.orderId}>
                      {/* Main Order Row */}
                      <tr className="order-main-row">
                        <td>
                          <IconButton
                            size="small"
                            onClick={() => toggleOrderExpand(order.orderId)}
                            sx={{ padding: '4px' }}
                          >
                            {isExpanded ? "▼" : "▶"}
                          </IconButton>
                        </td>
                        <td className="order-id-cell">
                          <Tooltip title={order.orderType === "INTERNATIONAL" ? "International Order" : "Domestic Order"}>
                            <span>{order.orderId}</span>
                          </Tooltip>
                        </td>
                        <td>
                          <div className="buyer-info-cell">
                            <span className="buyer-name">{order.buyer?.name || "Unknown Buyer"}</span>
                            <span className="buyer-email">{order.buyer?.email || "No email"}</span>
                            <span style={{ fontSize: '11px', color: '#94a3b8' }}>ID: {order.buyer?.buyerId || "N/A"}</span>
                          </div>
                        </td>
                        <td className="amount-cell">
                          ₹{order.pricing?.totalAmount?.toLocaleString() || "0"}
                        </td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {getOrderStatusIcon(summaryStatus)}
                            <span className={`order-status-badge ${getStatusClass(summaryStatus)}`}>
                              {summaryStatus.replace(/_/g, ' ')}
                            </span>
                            <Tooltip title={`${order.items?.filter(i => i.itemStatus !== "DELIVERED").length || 0} items pending`}>
                              <span style={{ fontSize: '12px', color: '#64748b' }}>
                                ({order.items?.length || 0} items)
                              </span>
                            </Tooltip>
                          </div>
                        </td>
                        <td>
                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <span style={{ fontSize: '12px', fontWeight: '600' }}>{order.payment?.mode || "N/A"}</span>
                            <span style={{ fontSize: '11px', color: '#64748b' }}>{order.payment?.paymentStatus || "pending"}</span>
                          </div>
                        </td>
                        <td>
                          <div style={{ fontSize: '13px' }}>
                            {new Date(order.createdAt).toLocaleDateString()}
                            <div style={{ fontSize: '11px', color: '#94a3b8' }}>{new Date(order.createdAt).toLocaleTimeString()}</div>
                          </div>
                        </td>
                        <td>
                          <button
                            className="order-btn-secondary"
                            style={{ padding: '6px 12px' }}
                            onClick={() => setSelectedOrder(order)}
                          >
                            <Visibility style={{ fontSize: '18px' }} /> View
                          </button>
                        </td>
                      </tr>

                      {/* Expanded Items Row */}
                      {isExpanded && (
                        <tr className="order-items-row">
                          <td colSpan="8" style={{ padding: '0', backgroundColor: '#f9fafb' }}>
                            <div style={{ padding: '16px 24px' }}>
                              <Typography variant="subtitle2" fontWeight="600" gutterBottom>
                                Order Items ({order.items?.length} items)
                              </Typography>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                {order.items?.map((item, idx) => {
                                  const sellerDetailsKey = `${order.orderId}-${idx}`;
                                  const isSellerDetailsExpanded = expandedSellerDetails.has(sellerDetailsKey);
                                  const toggleSellerDetails = () => {
                                    const newExpanded = new Set(expandedSellerDetails);
                                    if (newExpanded.has(sellerDetailsKey)) {
                                      newExpanded.delete(sellerDetailsKey);
                                    } else {
                                      newExpanded.add(sellerDetailsKey);
                                    }
                                    setExpandedSellerDetails(newExpanded);
                                  };
                                  return (
                                    <Paper
                                      key={idx}
                                      variant="outlined"
                                      sx={{
                                        p: 2,
                                        borderRadius: "12px",
                                        display: 'flex',
                                        flexDirection: 'column',
                                        backgroundColor: '#ffffff'
                                      }}
                                    >
                                      <div style={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                                        <img
                                          src={item.image || "https://via.placeholder.com/60"}
                                          alt={item.name}
                                          style={{ width: "60px", height: "60px", objectFit: "cover", borderRadius: "8px" }}
                                        />
                                        <Box flex={1}>
                                          <Typography variant="subtitle2" fontWeight="700">{item.name}</Typography>
                                          <Typography variant="caption" color="#64748b" display="block">
                                            Product ID: {item.productId} | Qty: {item.quantity} × ₹{item.price?.toLocaleString()}
                                          </Typography>
                                          {item.dimension && (
                                            <Typography variant="caption" color="#64748b" display="block">
                                              Dimensions: {item.dimension.length}×{item.dimension.breadth}×{item.dimension.height} cm,
                                              Weight: {item.dimension.weight} kg
                                            </Typography>
                                          )}
                                          {item.shippingCost > 0 && (
                                            <Typography variant="caption" color="#64748b" display="block">
                                              Shipping Cost: ₹{item.shippingCost.toLocaleString()}
                                            </Typography>
                                          )}
                                          {item.shipment?.awbCode && (
                                            <Typography variant="caption" color="#3b82f6" display="block">
                                              AWB: {item.shipment.awbCode}
                                              {item.shipment.trackingUrl && (
                                                <Link href={item.shipment.trackingUrl} target="_blank" rel="noopener noreferrer">
                                                  (Track)
                                                </Link>
                                              )}
                                            </Typography>
                                          )}
                                          {item.invoice?.invoiceUrl && (
                                            <Typography variant="caption" display="block">
                                              <Link href={item.invoice.invoiceUrl} target="_blank" rel="noopener noreferrer">
                                                Invoice #{item.invoice.invoiceNo}
                                              </Link>
                                            </Typography>
                                          )}
                                          {item.cancelReason && (
                                            <Typography variant="caption" color="error" display="block">
                                              Cancel Reason: {item.cancelReason}
                                            </Typography>
                                          )}
                                          {item.rejectReason && (
                                            <Typography variant="caption" color="error" display="block">
                                              Reject Reason: {item.rejectReason}
                                            </Typography>
                                          )}
                                        </Box>
                                        <div style={{ textAlign: 'right' }}>
                                          <Chip
                                            label={item.itemStatus}
                                            size="small"
                                            className={getStatusClass(item.itemStatus)}
                                            sx={{ fontWeight: 600 }}
                                          />
                                          {item.payout?.payoutStatus === "PAID" && (
                                            <Typography variant="caption" color="#10b981" display="block" mt={1}>
                                              Payout: ₹{item.payout.payoutAmount?.toLocaleString()}
                                            </Typography>
                                          )}
                                          <IconButton
                                            size="small"
                                            onClick={toggleSellerDetails}
                                            sx={{ mt: 1 }}
                                          >
                                            {isSellerDetailsExpanded ? <ExpandLess /> : <ExpandMore />}
                                            <Typography variant="caption">Seller</Typography>
                                          </IconButton>
                                        </div>
                                      </div>

                                      <Collapse in={isSellerDetailsExpanded}>
                                        <Divider sx={{ my: 1 }} />
                                        <Typography variant="body2" fontWeight="600">Seller Details</Typography>
                                        <Grid container spacing={1} sx={{ mt: 0.5 }}>
                                          <Grid size={{ xs: 12, sm: 6 }}>
                                            <Typography variant="caption" display="block"><strong>Name:</strong> {item.seller?.name || item.sellerId}</Typography>
                                            <Typography variant="caption" display="block"><strong>Company:</strong> {item.seller?.companyName || "N/A"}</Typography>
                                            <Typography variant="caption" display="block"><strong>Email:</strong> {item.seller?.email || "N/A"}</Typography>
                                            <Typography variant="caption" display="block"><strong>Phone:</strong> {item.seller?.phoneNumber || "N/A"}</Typography>
                                          </Grid>
                                          <Grid size={{ xs: 12, sm: 6 }}>
                                            <Typography variant="caption" display="block"><strong>GST:</strong> {item.seller?.gstNumber || "N/A"}</Typography>
                                            <Typography variant="caption" display="block"><strong>PAN:</strong> {item.seller?.companyPan || "N/A"}</Typography>
                                            <Typography variant="caption" display="block"><strong>Bank:</strong> {item.seller?.bankName || "N/A"}</Typography>
                                            <Typography variant="caption" display="block"><strong>Shiprocket Sync:</strong> {item.seller?.shiprocketSyncSuccess ? "Yes" : "No"}</Typography>
                                          </Grid>
                                        </Grid>
                                      </Collapse>
                                    </Paper>
                                  );
                                })}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="8" style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                    No orders found matching your filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination Footer */}
      {totalPages > 1 && (
        <div className="order-pagination">
          <button
            className="order-pagination-btn"
            disabled={!hasPreviousPage}
            onClick={() => setCurrentPage(p => p - 1)}
          >
            ← Previous
          </button>

          <div className="order-page-info">
            Page {currentPage} of {totalPages}
            <div className="order-showing-info">
              Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1}-
              {Math.min(currentPage * ITEMS_PER_PAGE, filteredOrders.length)} of {filteredOrders.length} orders
            </div>
          </div>

          <button
            className="order-pagination-btn"
            disabled={!hasNextPage}
            onClick={() => setCurrentPage(p => p + 1)}
          >
            Next →
          </button>
        </div>
      )}

      {/* Order Details Modal - Enhanced */}
      <Modal open={!!selectedOrder} onClose={() => setSelectedOrder(null)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "95%", md: "85%" },
            maxWidth: 1200,
            maxHeight: "90vh",
            overflow: "auto",
            bgcolor: "white",
            borderRadius: "16px",
            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
            outline: "none",
          }}
        >
          {selectedOrder && (
            <>
              <div className="order-modal-header">
                <Typography variant="h5" fontWeight="700" color="#1a365d">
                  Order Details: {selectedOrder.orderId}
                </Typography>
                <Typography variant="body2" color="#64748b">
                  Placed on {new Date(selectedOrder.createdAt).toLocaleString()} |
                  Type: {selectedOrder.orderType}
                </Typography>
              </div>

              <div className="order-modal-body">
                <Grid container spacing={4}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" fontWeight="600" gutterBottom color="#1e293b">
                      Buyer Information
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: "12px", bgcolor: "#f8fafc" }}>
                      <Typography variant="body2" mb={1}><strong>Name:</strong> {selectedOrder.buyer?.name || "N/A"}</Typography>
                      <Typography variant="body2" mb={1}><strong>Email:</strong> {selectedOrder.buyer?.email || "N/A"}</Typography>
                      <Typography variant="body2" mb={1}><strong>Phone:</strong> {selectedOrder.buyer?.phone || "N/A"}</Typography>
                      <Typography variant="body2"><strong>Buyer ID:</strong> {selectedOrder.buyer?.buyerId || "N/A"}</Typography>
                    </Paper>

                    {selectedOrder.buyer?.address && (
                      <Box mt={3}>
                        <Typography variant="subtitle1" fontWeight="600" gutterBottom color="#1e293b">
                          Shipping Address
                        </Typography>
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: "12px", bgcolor: "#f8fafc" }}>
                          <Typography variant="body2">
                            {selectedOrder.buyer.address.street}
                          </Typography>
                          <Typography variant="body2">
                            {selectedOrder.buyer.address.city}, {selectedOrder.buyer.address.state}
                          </Typography>
                          <Typography variant="body2">
                            {selectedOrder.buyer.address.postalCode}, {selectedOrder.buyer.address.country}
                          </Typography>
                        </Paper>
                      </Box>
                    )}
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" fontWeight="600" gutterBottom color="#1e293b">
                      Payment & Summary
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: "12px", bgcolor: "#f8fafc" }}>
                      <Typography variant="body2" mb={1}><strong>Mode:</strong> {selectedOrder.payment?.mode || "N/A"}</Typography>
                      <Typography variant="body2" mb={1}><strong>Payment Status:</strong> {selectedOrder.payment?.paymentStatus || "N/A"}</Typography>
                      <Typography variant="body2" mb={1}>
                        <strong>Subtotal:</strong> ₹{selectedOrder.pricing?.subtotal?.toLocaleString() || "0"}
                      </Typography>
                      <Typography variant="body2" mb={1}>
                        <strong>Shipping:</strong> ₹{selectedOrder.pricing?.shippingCost?.toLocaleString() || "0"}
                      </Typography>
                      <Typography variant="body2" mb={1}>
                        <strong>Total:</strong> ₹{selectedOrder.pricing?.totalAmount?.toLocaleString() || "0"}
                      </Typography>
                      <Typography variant="body2"><strong>Transaction ID:</strong> {selectedOrder.payment?.transactionId || "N/A"}</Typography>
                    </Paper>

                    {selectedOrder.commission && (
                      <Box mt={3}>
                        <Typography variant="subtitle1" fontWeight="600" gutterBottom color="#1e293b">
                          Commission & Earnings
                        </Typography>
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: "12px", bgcolor: "#f8fafc" }}>
                          <Typography variant="body2" mb={1}>
                            <strong>Commission:</strong> {selectedOrder.commission.percentage}% (₹{selectedOrder.commission.amount?.toLocaleString()})
                          </Typography>
                          <Typography variant="body2">
                            <strong>Seller Earning:</strong> ₹{selectedOrder.sellerEarning?.toLocaleString() || "0"}
                          </Typography>
                        </Paper>
                      </Box>
                    )}
                  </Grid>
                </Grid>

                <Divider sx={{ my: 4 }} />

                <Typography variant="subtitle1" fontWeight="600" gutterBottom color="#1e293b">
                  Order Items with Individual Status
                </Typography>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {selectedOrder.items?.map((item, idx) => {
                    const modalSellerDetailsKey = `modal-${selectedOrder.orderId}-${idx}`;
                    const isModalSellerDetailsExpanded = expandedSellerDetails.has(modalSellerDetailsKey);
                    const toggleModalSellerDetails = () => {
                      const newExpanded = new Set(expandedSellerDetails);
                      if (newExpanded.has(modalSellerDetailsKey)) {
                        newExpanded.delete(modalSellerDetailsKey);
                      } else {
                        newExpanded.add(modalSellerDetailsKey);
                      }
                      setExpandedSellerDetails(newExpanded);
                    };
                    return (
                      <Paper
                        key={idx}
                        variant="outlined"
                        sx={{ p: 2, borderRadius: "12px" }}
                      >
                        <Grid container spacing={2}>
                          <Grid item xs={2}>
                            <img
                              src={item.image || "https://via.placeholder.com/80"}
                              alt={item.name}
                              style={{ width: "100%", maxWidth: "80px", objectFit: "cover", borderRadius: "8px" }}
                            />
                          </Grid>
                          <Grid item xs={7}>
                            <Typography variant="subtitle2" fontWeight="700">{item.name}</Typography>
                            <Typography variant="caption" color="#64748b" display="block">
                              Product ID: {item.productId} | Seller: {item.sellerId}
                            </Typography>
                            <Typography variant="caption" color="#64748b" display="block">
                              Quantity: {item.quantity} × ₹{item.price?.toLocaleString()} = ₹{(item.price * item.quantity).toLocaleString()}
                            </Typography>
                            {item.dimension && (
                              <Typography variant="caption" color="#64748b" display="block">
                                Dimensions: {item.dimension.length}×{item.dimension.breadth}×{item.dimension.height} cm,
                                Weight: {item.dimension.weight} kg
                              </Typography>
                            )}
                            {item.shippingCost > 0 && (
                              <Typography variant="caption" color="#64748b" display="block">
                                Shipping Cost: ₹{item.shippingCost.toLocaleString()}
                              </Typography>
                            )}
                            {item.shipment?.awbCode && (
                              <Typography variant="caption" color="#3b82f6" display="block">
                                AWB: {item.shipment.awbCode}
                                {item.shipment.trackingUrl && (
                                  <Link href={item.shipment.trackingUrl} target="_blank" rel="noopener noreferrer">
                                    (Track)
                                  </Link>
                                )}
                              </Typography>
                            )}
                            {item.invoice?.invoiceUrl && (
                              <Typography variant="caption" display="block">
                                <Link href={item.invoice.invoiceUrl} target="_blank" rel="noopener noreferrer">
                                  Invoice #{item.invoice.invoiceNo}
                                </Link>
                              </Typography>
                            )}
                            {item.cancelReason && (
                              <Typography variant="caption" color="error" display="block">
                                Cancel Reason: {item.cancelReason}
                              </Typography>
                            )}
                            {item.rejectReason && (
                              <Typography variant="caption" color="error" display="block">
                                Reject Reason: {item.rejectReason}
                              </Typography>
                            )}
                            <IconButton
                              size="small"
                              onClick={toggleModalSellerDetails}
                              sx={{ mt: 1 }}
                            >
                              {isModalSellerDetailsExpanded ? <ExpandLess /> : <ExpandMore />}
                              <Typography variant="caption">Seller Details</Typography>
                            </IconButton>
                            <Collapse in={isModalSellerDetailsExpanded}>
                              <Divider sx={{ my: 1 }} />
                              <Typography variant="body2" fontWeight="600">Seller Information</Typography>
                              <Grid container spacing={1} sx={{ mt: 0.5 }}>
                                <Grid item xs={12} sm={6}>
                                  <Typography variant="caption" display="block"><strong>Name:</strong> {item.seller?.name || item.sellerId}</Typography>
                                  <Typography variant="caption" display="block"><strong>Company:</strong> {item.seller?.companyName || "N/A"}</Typography>
                                  <Typography variant="caption" display="block"><strong>Email:</strong> {item.seller?.email || "N/A"}</Typography>
                                  <Typography variant="caption" display="block"><strong>Phone:</strong> {item.seller?.phoneNumber || "N/A"}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                  <Typography variant="caption" display="block"><strong>GST:</strong> {item.seller?.gstNumber || "N/A"}</Typography>
                                  <Typography variant="caption" display="block"><strong>PAN:</strong> {item.seller?.companyPan || "N/A"}</Typography>
                                  <Typography variant="caption" display="block"><strong>Bank:</strong> {item.seller?.bankName || "N/A"}</Typography>
                                  <Typography variant="caption" display="block"><strong>Shiprocket Sync:</strong> {item.seller?.shiprocketSyncSuccess ? "Yes" : "No"}</Typography>
                                </Grid>
                              </Grid>
                            </Collapse>
                          </Grid>
                          <Grid item xs={3} style={{ textAlign: 'right' }}>
                            <Chip
                              label={item.itemStatus}
                              className={getStatusClass(item.itemStatus)}
                              sx={{ fontWeight: 600, mb: 1 }}
                            />
                            {item.payout?.payoutStatus === "PAID" && (
                              <Typography variant="caption" color="#10b981" display="block">
                                Payout: ₹{item.payout.payoutAmount?.toLocaleString()}
                              </Typography>
                            )}
                            {item.shipment?.shipmentStatus && (
                              <Typography variant="caption" color="#64748b" display="block">
                                Shipment: {item.shipment.shipmentStatus}
                              </Typography>
                            )}
                          </Grid>
                        </Grid>
                      </Paper>
                    );
                  })}
                </div>

                {/* Additional sections: Returns and Customs */}
                {selectedOrder.returns && selectedOrder.returns.length > 0 && (
                  <>
                    <Divider sx={{ my: 4 }} />
                    <Typography variant="subtitle1" fontWeight="600" gutterBottom color="#1e293b">
                      Returns
                    </Typography>
                    {selectedOrder.returns.map((ret, idx) => (
                      <Paper key={idx} variant="outlined" sx={{ p: 2, borderRadius: "12px", mb: 2 }}>
                        <Typography variant="body2"><strong>Product ID:</strong> {ret.productId}</Typography>
                        <Typography variant="body2"><strong>Reason:</strong> {ret.reason}</Typography>
                        <Typography variant="body2"><strong>Status:</strong> {ret.status}</Typography>
                        <Typography variant="body2"><strong>Refund Amount:</strong> ₹{ret.refundAmount?.toLocaleString()}</Typography>
                        {ret.shiprocket && (
                          <Typography variant="body2"><strong>Return AWB:</strong> {ret.shiprocket.returnOrderId}</Typography>
                        )}
                      </Paper>
                    ))}
                  </>
                )}

                {selectedOrder.customs && (
                  <>
                    <Divider sx={{ my: 4 }} />
                    <Typography variant="subtitle1" fontWeight="600" gutterBottom color="#1e293b">
                      Customs Information
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: "12px" }}>
                      <Typography variant="body2"><strong>HS Code:</strong> {selectedOrder.customs.hsCode}</Typography>
                      <Typography variant="body2"><strong>Country of Origin:</strong> {selectedOrder.customs.countryOfOrigin}</Typography>
                      <Typography variant="body2"><strong>Description:</strong> {selectedOrder.customs.description}</Typography>
                      <Typography variant="body2"><strong>Declared Value:</strong> ₹{selectedOrder.customs.declaredValue?.toLocaleString()}</Typography>
                    </Paper>
                  </>
                )}

                <Box mt={4} display="flex" justifyContent="flex-end">
                  <Button
                    variant="outlined"
                    onClick={() => setSelectedOrder(null)}
                    sx={{ borderRadius: "8px", px: 4 }}
                  >
                    Close
                  </Button>
                </Box>
              </div>
            </>
          )}
        </Box>
      </Modal>
    </div>
  );
}